import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import CssBaseline from "@mui/material/CssBaseline";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import App from "./App";
import client from "./apolloClient";
import { ThemeProvider } from "./contexts/ThemeContext";
import "./i18n";
import { useTranslation } from "react-i18next";
import { NotificationProvider } from "./contexts/NotificationContext";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { Box, Button, Slide, Typography } from "@mui/material";
import { Download } from "@mui/icons-material";

// Create separate caches for RTL and LTR
const rtlCache = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

const ltrCache = createCache({
  key: "muiltr",
  stylisPlugins: [prefixer],
});

const DirectionProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const direction = i18n.dir();

  return (
    <CacheProvider value={direction === "rtl" ? rtlCache : ltrCache}>
      {children}
    </CacheProvider>
  );
};

// New component to handle the install prompt
const InstallPrompt = () => {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      // console.log("beforeinstallprompt event fired"); // Add logging
      setDeferredPrompt(e);
      setOpen(true);
    };

    // Check if browser supports the event
    if (window.matchMedia("(display-mode: browser)").matches) {
      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  useEffect(() => {
    // console.log("Install prompt state:", { open, deferredPrompt }); // Debug state
  }, [open, deferredPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      // console.log(`User response: ${outcome}`);
    } catch (error) {
      // console.error("Installation failed:", error);
    } finally {
      setDeferredPrompt(null);
      setOpen(false);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      autoHideDuration={null}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      TransitionComponent={Slide} // Add slide animation
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={handleClose}
        sx={{
          width: "100%",
          maxWidth: "400px",
          alignItems: "center",
          background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
          color: "white",
          boxShadow: 6,
          borderRadius: "12px",
          ".MuiAlert-icon": {
            color: "white",
            fontSize: "28px",
            alignItems: "center",
            mr: 2,
          },
          ".MuiAlert-action": { paddingLeft: 0 },
        }}
        icon={<Download fontSize="large" />} // Add custom icon
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 0.5, color: "white" }}
            >
              Upgrade Your Experience! ✨
            </Typography>
            <Typography variant="body2" sx={{ color: "white" }}>
              Get faster access and exclusive features with our app
            </Typography>
          </Box>
          <Button
            variant="containedWhite" // Use new variant
            onClick={handleInstall}
            sx={{
              ml: 3,
              fontWeight: "bold",
              borderRadius: "8px",
              px: 3,
              py: 1,
              border: "1px solid #e0e0e0",
              boxShadow: 2,
              transform: "translateY(0)",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: 4,
              },
            }}
          >
            Install Now
          </Button>
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: -10,
            right: -10,
            bgcolor: "#ef4444",
            color: "white",
            fontSize: "0.75rem",
            fontWeight: "bold",
            px: 1,
            borderRadius: "4px",
            boxShadow: 1,
          }}
        >
          NEW!
        </Box>
      </Alert>
    </Snackbar>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <DirectionProvider>
    <ApolloProvider client={client}>
      <NotificationProvider>
        <ThemeProvider>
          <CssBaseline />
          <Router>
            <App />
            <InstallPrompt />
          </Router>
        </ThemeProvider>
      </NotificationProvider>
    </ApolloProvider>
  </DirectionProvider>
);
